import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Star, ArrowRight } from 'lucide-react';
import api, { API_BASE_URL } from '../../utils/api';

interface Worker {
  id: number;
  name: string;
  firstName: string;
  lastName: string;
  profilePicture?: string;
  parish: string;
  rating: number;
  totalReviews: number;
  experienceYears: number;
  verificationStatus: string;
  membershipTier?: string;
  membershipTierDisplay?: string;
  verifiedBadge?: boolean;
}

interface Service {
  id: number;
  title: string;
  image: string;
  description: string;
  price: number;
  duration: string;
  category: string;
  createdAt: string;
  worker: Worker;
}

interface RecentServicesProps {
  limit?: number;
  showViewAll?: boolean;
}

const RecentServices: React.FC<RecentServicesProps> = ({ limit = 6, showViewAll = true }) => {
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchRecentServices();
  }, []);

  const fetchRecentServices = async () => {
    try {
      setLoading(true);
      setError(null);

      const data = await api.get('/api/services');

      // console.log(data, 'services');

      if (data.success) {
        const sortedServices = data.services
          .sort(
            (a: Service, b: Service) =>
              new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          )
          .slice(0, limit);
        setServices(sortedServices);
      } else {
        throw new Error(data.message || 'Failed to fetch services');
      }
    } catch (error) {
      console.error('Error fetching recent services:', error);
      setError(error instanceof Error ? error.message : 'Failed to load services');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handles 3 cases:
   * 1. Empty / null / undefined  → fallback image
   * 2. Already a full URL (http/https) → use as-is
   * 3. Relative filename          → prepend API_BASE_URL/uploads/
   */
    const getImageUrl = (image?: string): string => {
    if (!image || image.trim() === '') {
      return '/assets/images/service-one.png';
    }

    if (image.startsWith('http://') || image.startsWith('https://')) {
      return image;
    }

    // Derive uploads base separately — support localhost, 127.0.0.1 and LAN IPs
    const h = window.location.hostname;
    const isLocal =
      h === 'localhost' || h === '127.0.0.1' ||
      /^192\.168\./.test(h) || /^10\./.test(h) ||
      /^172\.(1[6-9]|2[0-9]|3[01])\./.test(h);
    const uploadsBase = process.env.REACT_APP_API_URL
      ? process.env.REACT_APP_API_URL.replace(/\/api$/, '')
      : isLocal
        ? `http://${h}:5000`
        : 'https://api.skillseek.co';

    if (image.startsWith('/uploads/') || image.startsWith('uploads/')) {
      const cleanPath = image.startsWith('/') ? image.substring(1) : image;
      return `${uploadsBase}/${cleanPath}`;
    }

    return `${uploadsBase}/uploads/${image}`;
  };

  const formatPrice = (price: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  if (loading) {
    return (
      <section className="premium-services-sec">
        <div className="premium-services-header">
          <div>
            <h2 className="premium-section-title">Our Recent <span>Services</span></h2>
            <p className="premium-section-subtitle">Each listing is designed to be clear and concise, providing customers with peace of mind.</p>
          </div>
        </div>
        <div className="premium-services-grid">
          {[1, 2, 3].map((i) => (
            <div key={i} className="premium-service-card" style={{ height: '300px' }}>
              <div className="loading-container" style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div className="loading-spinner"></div>
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="premium-services-sec">
        <div className="premium-services-header">
          <div>
            <h2 className="premium-section-title">Our Recent <span>Services</span></h2>
            <p className="premium-section-subtitle">Each listing is designed to be clear and concise, providing customers with peace of mind.</p>
          </div>
        </div>
        <div className="text-center p-5 border rounded-lg bg-light">
          <h5>Unable to load services</h5>
          <p className="text-muted">{error}</p>
          <button onClick={fetchRecentServices} className="premium-btn-outline mt-3">
            Try Again
          </button>
        </div>
      </section>
    );
  }

  if (services.length === 0) {
    return (
      <section className="premium-services-sec">
        <div className="premium-services-header">
          <div>
            <h2 className="premium-section-title">Our Recent <span>Services</span></h2>
            <p className="premium-section-subtitle">Each listing is designed to be clear and concise, providing customers with peace of mind.</p>
          </div>
        </div>
        <div className="text-center p-5 border rounded-lg bg-light">
          <h5 className="text-muted">No services available</h5>
          <p className="text-muted">New services will appear here as they become available.</p>
        </div>
      </section>
    );
  }

  return (
    <section className="premium-services-sec">
      <div className="premium-services-header">
        <div>
          <h2 className="premium-section-title">Our Recent <span>Services</span></h2>
          <p className="premium-section-subtitle">Each listing is designed to be clear and concise, providing customers with peace of mind.</p>
        </div>
        {showViewAll && (
          <Link to="/listing" className="premium-view-all-link d-none d-md-inline-flex">
            <span>View all</span>
            <ArrowRight size={14} />
          </Link>
        )}
      </div>

      <div className="premium-services-grid">
        {services.map((service) => (
          <Link key={service.id} to={`/service/${service.id}`} className="premium-service-card">
            <img
              className="premium-service-img"
              src={getImageUrl(service.image)}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = '/assets/images/service-one.png';
              }}
              alt={service.title}
            />
            <div className="premium-service-body">
              <h3 className="premium-service-title">{service.title}</h3>
              <div className="premium-service-footer">
                <div className="premium-service-price">
                  Service starts at <span>{formatPrice(service.price)}</span>
                </div>
                <div className="premium-service-rating">
                  <Star size={14} style={{ fill: '#EF9F27', stroke: '#EF9F27' }} />
                  <span>{service.worker.rating.toFixed(1)}</span>
                  <span className="count">({service.worker.totalReviews || 0})</span>
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {showViewAll && (
        <div className="text-center mt-4 d-md-none">
          <Link to="/listing" className="premium-view-all-link">
            <span>View all</span>
            <ArrowRight size={14} />
          </Link>
        </div>
      )}
    </section>
  );
};

export default RecentServices;